import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, Injector } from '@angular/core';
import { UserService } from './shared/services/user.service';
import { ApiService } from './shared/services/api.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { MinimizedMeetingService } from './core/meetings/services/minimized-meeting.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AppConstants } from './shared/constants/app.contants';
import { environment } from 'src/environments/environment';
import { SessionService } from './shared/services/session.service';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'] 
})
export class AppComponent {
  isWorkflowTaskRoute = false;

  constructor(
    private _userService: UserService,
    private apiService: ApiService,
    private router: Router,
    private injector: Injector,
    private minimizedMeetingService: MinimizedMeetingService,
    private jwtHelper: JwtHelperService,
    private http: HttpClient,
    private sessionService: SessionService
  ) {
    // Set injector in user service for checking active meetings after login
    this._userService.setInjector(this.injector);
    
    this.router.events
    .pipe(filter(event => event instanceof NavigationEnd))
    .subscribe(event => {
      console.log('Navigated to:', event);
      
      // Check for active meetings after navigation (especially after login)
      const navEvent = event as NavigationEnd;
      this.isWorkflowTaskRoute = this.isPublicWorkflowTaskUrl(navEvent.urlAfterRedirects || navEvent.url);
      if (this.isWorkflowTaskRoute) {
        return;
      }

      if (this._userService.isUserAuthenticated) {
        this.sessionService.startAutoRefresh();
      }
      
      // Only check on navigation to home/main or after login (not on every navigation)
      // Don't check if navigating to meetings page (meetings component will handle it)
      if ((navEvent.url.includes('/home') || navEvent.url === '/' || navEvent.url.includes('/main')) 
          && !navEvent.url.includes('/meetings')) {
        // Wait a bit to ensure user is fully authenticated and loaded
        setTimeout(() => {
          // Try multiple ways to get email
          let userEmail = this._userService.user?.EmailAddress || 
                         this._userService.email || '';
          
          // Fallback: try to get email from token if not available
          if (!userEmail && this._userService.token) {
            try {
              const decodedToken = this.jwtHelper.decodeToken(this._userService.token);
              userEmail = decodedToken?.[AppConstants.CLAIMS.EmailAddress] || 
                         decodedToken?.['EmailAddress'] || 
                         decodedToken?.['email'] || 
                         decodedToken?.['Email'] || '';
            } catch (e) {
              console.warn('Could not decode token for email:', e);
            }
          }
          
          if (userEmail && this._userService.isUserAuthenticated) {
            console.log('Checking active meetings on navigation for:', userEmail);
            // Only call checkActiveMeetingsAfterLogin - it will fetch meetings and validate
            // Don't call validateAndCleanupMeetings separately to avoid duplicate API calls
            this.minimizedMeetingService.checkActiveMeetingsAfterLogin(userEmail).catch(err => {
              console.error('Error checking active meetings:', err);
            });
          } else {
            console.warn('User not authenticated or email not available:', {
              hasEmail: !!userEmail,
              isAuthenticated: this._userService.isUserAuthenticated,
              user: this._userService.user,
              token: !!this._userService.token
            });
            // If user not authenticated, still validate and cleanup (but this won't call API if no minimized meetings)
            this.minimizedMeetingService.validateAndCleanupMeetings().catch(err => {
              console.error('Error validating meetings on navigation:', err);
            });
          }
        }, 1000); // Delay to ensure everything is loaded
      }
    });

  }
  title = 'bidaya-tsm';

  ngOnInit() {
    this.isWorkflowTaskRoute = this.isPublicWorkflowTaskUrl(this.router.url || `${window.location.pathname}${window.location.search}`);
    // this.apiService.loadAllLookups().then(() => {
    //   console.log('Lookups loaded successfully');
    // }).catch(error => {
    //   console.error('Failed to load lookups:', error);
    // });
    this._userService.initializeFromLocalStorage();

    if (this.isWorkflowTaskRoute) {
      return;
    }

    this.validateSession();
    
    // Validate and clean up stale meetings from storage first
    setTimeout(() => {
      this.minimizedMeetingService.validateAndCleanupMeetings().catch(err => {
        console.error('Error validating meetings on init:', err);
      });
    }, 500); // Small delay to ensure service is initialized
    
    // Check for active meetings if user is already authenticated (e.g., page refresh)
    setTimeout(() => {
      const userEmail = this._userService.user?.EmailAddress || this._userService.email || '';
      if (userEmail && this._userService.isUserAuthenticated) {
        this.minimizedMeetingService.checkActiveMeetingsAfterLogin(userEmail).catch(err => {
          console.error('Error checking active meetings on init:', err);
        });
      }
    }, 1500); // Delay to ensure everything is initialized
  }

  private isPublicWorkflowTaskUrl(url: string): boolean {
    return url.includes('/vendor-task/') && url.includes('taskToken=');
  }

  private validateSession(): void {
    const token = this._userService.token || localStorage.getItem('QNBNToken');
    if (!token) {
      return;
    }

    if (this.jwtHelper.isTokenExpired(token)) {
      this.sessionService.refreshSession().subscribe(refreshed => {
        if (!refreshed) {
          this.sessionService.endSession();
        }
      });
      return;
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'X-Skip-Retry': 'true',
      'X-Skip-Session-Refresh': 'true'
    });

    this.http.get<any>(`${environment.API_URL}/${environment.APIs_PREFIX}/User/ValidateSession`, { headers })
      .subscribe({
        next: response => {
          if (response?.isSuccess !== true || response?.data !== true) {
            this.sessionService.endSession();
            return;
          }

          this.sessionService.startAutoRefresh();
        },
        error: () => this.sessionService.endSession()
      });
  }
}


